import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Send } from 'lucide-react';

import {
  getAdminEthicsReport,
  updateReportStatus,
  assignReport,
  updateReportPriority,
  sendAdminMessage,
} from '../../services/ethicsReportService';
import api from '../../services/api';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import TextInput from '../../components/ui/TextInput';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FieldCol = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Label = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: #64748b;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color + '18'};
  color: ${({ $color }) => $color};
`;

const MessagesContainer = styled.div`
  max-height: 450px;
  overflow-y: auto;
  padding: 8px 0;
  margin-bottom: 16px;
`;

const MessageBubble = styled.div`
  max-width: 75%;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.5;
  ${({ $isInvestigator, theme }) =>
    $isInvestigator
      ? `
        margin-left: auto;
        background: ${theme.colors.primary.main};
        color: #fff;
        border-bottom-right-radius: 2px;
      `
      : `
        margin-right: auto;
        background: #F1F5F9;
        color: ${theme.colors.text.primary};
        border-bottom-left-radius: 2px;
      `
  }
`;

const MessageMeta = styled.div`
  font-size: 11px;
  color: ${({ $isInvestigator }) => $isInvestigator ? 'rgba(255,255,255,0.7)' : '#94a3b8'};
  margin-top: 4px;
`;

const MessageInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

// #72 — bkz. EthicsReportTrackPage.js'in AYNI yorumu (yönetici tarafının
// eşdeğeri) — yerel SendButton kaldırıldı, IconButton'a bağlandı.

const TimelineItem = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const TimelineDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#3B82F6'};
  margin-top: 5px;
  flex-shrink: 0;
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TimelineDate = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`;

const TimelineNote = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #64748b;
  margin-top: 4px;
`;

const statusColors = {
  0: '#3B82F6', 1: '#8B5CF6', 2: '#F59E0B', 3: '#EF4444',
  4: '#10B981', 5: '#6B7280', 6: '#9CA3AF', 7: '#F97316',
};

const priorityColors = {
  0: '#6B7280', 1: '#F59E0B', 2: '#EF4444', 3: '#DC2626',
};

export default function EthicsReportDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();

  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const messagesEndRef = useRef(null);

  const statusLabels = useMemo(() => ({
    0: t('ethicsReport.statusNew'),
    1: t('ethicsReport.statusAcknowledged'),
    2: t('ethicsReport.statusUnderReview'),
    3: t('ethicsReport.statusInvestigation'),
    4: t('ethicsReport.statusActionTaken'),
    5: t('ethicsReport.statusClosed'),
    6: t('ethicsReport.statusUnfounded'),
    7: t('ethicsReport.statusAwaitingInfo'),
  }), [t]);

  const priorityLabels = useMemo(() => ({
    0: t('ethicsReport.priorityLow'),
    1: t('ethicsReport.priorityMedium'),
    2: t('ethicsReport.priorityHigh'),
    3: t('ethicsReport.priorityCritical'),
  }), [t]);

  const statusOptions = useMemo(() =>
    Object.entries(statusLabels).map(([k, v]) => ({ id: Number(k), name: v })),
    [statusLabels]
  );

  const priorityOptions = useMemo(() =>
    Object.entries(priorityLabels).map(([k, v]) => ({ id: Number(k), name: v })),
    [priorityLabels]
  );

  const fetchReport = useCallback(() => {
    getAdminEthicsReport(id).then((res) => {
      const data = res.data?.data;
      if (data) setReport(data);
    });
  }, [id]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (activeTab === 'messages' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTab, report?.messages?.length]);

  const handleStatusChange = useCallback(async (newStatus) => {
    try {
      await updateReportStatus(id, { status: newStatus, note: statusNote });
      setStatusNote('');
      fetchReport();
    } catch {
      // error handled by interceptor
    }
  }, [id, statusNote, fetchReport]);

  const handlePriorityChange = useCallback(async (newPriority) => {
    try {
      await updateReportPriority(id, { priority: newPriority });
      fetchReport();
    } catch {
      // error handled by interceptor
    }
  }, [id, fetchReport]);

  const handleAssign = useCallback(async (userId) => {
    try {
      await assignReport(id, { assignedToUserId: userId });
      fetchReport();
    } catch {
      // error handled by interceptor
    }
  }, [id, fetchReport]);

  const handleSendMessage = useCallback(async () => {
    if (!messageText.trim()) return;
    setSending(true);
    try {
      await sendAdminMessage(id, { messageText });
      setMessageText('');
      fetchReport();
    } catch {
      // error handled by interceptor
    } finally {
      setSending(false);
    }
  }, [id, messageText, fetchReport]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const searchEmployees = useCallback(async (term) => {
    const params = term ? { 'firstName[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    return (res.data?.data?.data || []).map((e) => ({
      id: e.id,
      name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
      profilePicture: e.profilePicture,
    }));
  }, []);

  const tabs = [
    { key: 'info', label: t('ethicsReport.info') },
    { key: 'messages', label: t('ethicsReport.messages') },
    { key: 'history', label: t('ethicsReport.history') },
  ];

  const reportTitle = report?.title || '...';

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('ethicsReport.adminTitle'), path: '/ethics-reports' },
          { label: reportTitle },
        ]} />
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
        {activeTab === 'info' && report && (
          <Card>
            <FieldRow>
              <FieldCol>
                <Label>{t('ethicsReport.trackingCode')}</Label>
                <Value style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{report.trackingCode}</Value>
              </FieldCol>
              <FieldCol>
                <Label>{t('ethicsReport.reportTitle')}</Label>
                <Value>{report.title}</Value>
              </FieldCol>
            </FieldRow>

            <FieldRow>
              <FieldCol>
                <Label>{t('ethicsReport.category')}</Label>
                <Value>{report.categoryName || '-'}</Value>
              </FieldCol>
              <FieldCol>
                <Label>{t('ethicsReport.reporter')}</Label>
                <Value>{report.isAnonymous ? t('ethicsReport.anonymousReporter') : (report.reporterName || '-')}</Value>
              </FieldCol>
            </FieldRow>

            <FieldRow>
              <FieldCol>
                <Label>{t('ethicsReport.description')}</Label>
                <Value>{report.description}</Value>
              </FieldCol>
            </FieldRow>

            <FieldRow>
              <FieldCol>
                <GenericSelect
                  label={t('ethicsReport.status')}
                  value={report.status}
                  onChange={handleStatusChange}
                  options={statusOptions}
                  idKey="id"
                  labelKey="name"
                />
              </FieldCol>
              <FieldCol>
                <GenericSelect
                  label={t('ethicsReport.priority')}
                  value={report.priority}
                  onChange={handlePriorityChange}
                  options={priorityOptions}
                  idKey="id"
                  labelKey="name"
                />
              </FieldCol>
            </FieldRow>

            <FieldRow>
              <FieldCol>
                <TextInput
                  label={t('ethicsReport.statusNote')}
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                />
              </FieldCol>
            </FieldRow>

            <FieldRow>
              <FieldCol>
                <AsyncGenericSelect
                  label={t('ethicsReport.assignedTo')}
                  value={report.assignedToUserId || ''}
                  onChange={handleAssign}
                  onSearch={searchEmployees}
                />
              </FieldCol>
            </FieldRow>
          </Card>
        )}

        {activeTab === 'messages' && report && (
          <Card>
            <MessagesContainer>
              {(report.messages || []).map((msg, idx) => {
                const isInvestigator = !msg.isReporter;
                return (
                  <MessageBubble key={idx} $isInvestigator={isInvestigator}>
                    {msg.messageText || msg.text}
                    <MessageMeta $isInvestigator={isInvestigator}>
                      {msg.isReporter
                        ? (report.isAnonymous ? t('ethicsReport.anonymousReporter') : (report.reporterName || t('ethicsReport.reporter')))
                        : (msg.senderName || t('ethicsReport.investigator'))}
                      {msg.createDate && ` - ${new Date(msg.createDate).toLocaleString()}`}
                    </MessageMeta>
                  </MessageBubble>
                );
              })}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <MessageInputRow>
              <div style={{ flex: 1 }}>
                <TextInput
                  label={t('ethicsReport.writeMessage')}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <Tooltip text={t('common.send')}>
                <IconButton
                  variant="gradient"
                  aria-label={t('common.send')}
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || sending}
                >
                  <Send size={18} />
                </IconButton>
              </Tooltip>
            </MessageInputRow>
          </Card>
        )}

        {activeTab === 'history' && report && (
          <Card>
            {(report.statusHistory || []).length === 0 && (
              <Value style={{ color: '#94a3b8' }}>{t('ethicsReport.noHistory')}</Value>
            )}
            {(report.statusHistory || []).map((entry, idx) => (
              <TimelineItem key={idx}>
                <TimelineDot $color={statusColors[entry.status] || '#3B82F6'} />
                <TimelineContent>
                  <TimelineTitle>
                    <StatusBadge $color={statusColors[entry.status] || '#6B7280'}>
                      {statusLabels[entry.status] || '-'}
                    </StatusBadge>
                  </TimelineTitle>
                  <TimelineDate>
                    {entry.createDate ? new Date(entry.createDate).toLocaleString() : ''}
                    {entry.changedByName && ` - ${entry.changedByName}`}
                  </TimelineDate>
                  {entry.note && <TimelineNote>{entry.note}</TimelineNote>}
                </TimelineContent>
              </TimelineItem>
            ))}
          </Card>
        )}
      </TabContent>
    </PageContainer>
  );
}
